import React, { type ElementType, type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import classnames from 'src/util/classnames';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  color?: string;
  body?: boolean;
  inverse?: boolean;
  outline?: boolean;
  tag?: ElementType;
  innerRef?: React.Ref<HTMLElement>;
  children?: ReactNode;
}

export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { color, body, inverse, outline, tag: Tag = 'div', innerRef, className, children, ...props },
  ref
) {
  return (
    <Tag
      ref={innerRef ?? ref}
      className={classnames(
        'card',
        inverse && 'text-white',
        body && 'card-body',
        color && `${outline ? 'border' : 'bg'}-${color}`,
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
});

export function CardBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classnames('card-body', className)} {...props}>{children}</div>;
}

export function CardHeader({ tag: Tag = 'div', className, children, ...props }: HTMLAttributes<HTMLDivElement> & { tag?: ElementType }) {
  return <Tag className={classnames('card-header', className)} {...props}>{children}</Tag>;
}

export function CardFooter({ tag: Tag = 'div', className, children, ...props }: HTMLAttributes<HTMLDivElement> & { tag?: ElementType }) {
  return <Tag className={classnames('card-footer', className)} {...props}>{children}</Tag>;
}

export function CardTitle({ tag: Tag = 'h5', className, children, ...props }: HTMLAttributes<HTMLHeadingElement> & { tag?: ElementType }) {
  return <Tag className={classnames('card-title', className)} {...props}>{children}</Tag>;
}

export function CardSubtitle({ tag: Tag = 'h6', className, children, ...props }: HTMLAttributes<HTMLHeadingElement> & { tag?: ElementType }) {
  return <Tag className={classnames('card-subtitle text-muted', className)} {...props}>{children}</Tag>;
}

export function CardText({ tag: Tag = 'p', className, children, ...props }: HTMLAttributes<HTMLParagraphElement> & { tag?: ElementType }) {
  return <Tag className={classnames('card-text', className)} {...props}>{children}</Tag>;
}

export function CardLink({ className, children, ...props }: HTMLAttributes<HTMLAnchorElement>) {
  return <a className={classnames('card-link', className)} {...props}>{children}</a>;
}

export function CardImg({ top, bottom, className, ...props }: React.ImgHTMLAttributes<HTMLImageElement> & { top?: boolean; bottom?: boolean }) {
  return (
    <img
      className={classnames(
        'card-img',
        top && 'card-img-top',
        bottom && 'card-img-bottom',
        className,
      )}
      {...props}
    />
  );
}

export function CardImgOverlay({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classnames('card-img-overlay', className)} {...props}>{children}</div>;
}
